import React, { useState, useRef, useEffect } from "react";
import Calendar from "react-calendar";
import { MdCalendarToday, MdClose } from "react-icons/md";
import "react-calendar/dist/Calendar.css";
import "assets/css/DateFilterPicker.css";
import { formatDateLong } from "../../utils/dateUtils";

interface DateFilterPickerProps {
  label: string;
  // YYYY-MM-DD, or undefined when no date is selected
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
}

// Local parts, not toISOString(), which shifts the day west of UTC.
const toDateParam = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Parse YYYY-MM-DD as a local date, not UTC midnight.
const fromDateParam = (value: string | undefined): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime()) ? null : date;
};

const DateFilterPicker: React.FC<DateFilterPickerProps> = ({
  label,
  value,
  onChange,
  placeholder = "Any date",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedDate = fromDateParam(value);

  const handleSelect = (next: any) => {
    const date = Array.isArray(next) ? next[0] : next;
    if (date instanceof Date) {
      onChange(toDateParam(date));
    }
    setIsOpen(false);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(undefined);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={pickerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={label}
        className={`flex w-full min-w-[170px] items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
          value
            ? "border-biafit-primary bg-biafit-primary bg-opacity-10 text-navy-700 dark:text-white"
            : "border-gray-600 bg-white text-gray-700 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300"
        } focus:border-transparent hover:border-biafit-primary focus:outline-none focus:ring-2 focus:ring-biafit-primary`}
      >
        <div className="flex min-w-0 flex-1 items-center space-x-2">
          <MdCalendarToday className="h-4 w-4 flex-shrink-0" />
          <span className="truncate font-medium">
            {value ? formatDateLong(value) : label}
          </span>
        </div>
        {value && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear date"
            onClick={handleClear}
            className="flex-shrink-0 rounded-full p-0.5 transition-colors hover:bg-biafit-primary hover:bg-opacity-20"
          >
            <MdClose className="h-4 w-4" />
          </span>
        )}
      </button>

      {isOpen && (
        <div className="biafit-date-picker absolute z-20 mt-1 w-[300px] rounded-md border border-gray-300 bg-white p-3 shadow-lg dark:border-gray-600 dark:bg-navy-800">
          <Calendar
            value={selectedDate}
            onChange={handleSelect}
            view="month"
            locale="en-US"
          />
          <button
            type="button"
            onClick={handleClear}
            className="mt-2 w-full rounded-md px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700"
          >
            {placeholder}
          </button>
        </div>
      )}
    </div>
  );
};

export default DateFilterPicker;
